import L from 'leaflet';
import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useDispatch } from 'react-redux';

import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import UserShapesModal from './UserShapesModal';
import { getColors } from '../../helpers/colorSelector';
import { dataGeoServiciosFetch } from '../../helpers/fetch';
import {
  getLegend,
  getLegendPoints,
  invertCoordinates,
} from '../../helpers/generalFunctions';
import {
  getAnnouncersLayerGroupHistoric,
  getAnnouncersLayerGroupRealtime,
} from '../gis/layerGroups/announcersLayerGroup';
import {
  getLightningsMarkerClusterGroupHistoric,
  getLightningsMarkerClusterGroupRealtime,
} from '../gis/layerGroups/lightningsMarkerClusterGroup';
import { getPointsLayerGroupRealtime } from '../gis/layerGroups/pointsLayerGroup';
import {
  addGeomanControl,
  getFullscreenControl,
  getLayersControl,
  getLegendsControlHistoric,
  getLegendsControlRealtime,
  getMousePositionControl,
  getRulerControl,
  getSaveControl,
  getScaleControl,
  getZoomHomeControl,
} from './controls/controls';
import './controls/css/legend.css';
import {
  getCircleRadiusLayerGroupHistoric,
  getCircleRadiusLayerGroupRealTime,
} from './layerGroups/circleRadiusLayerGroup';
import { getLineLightningsMarkerClusterGroupRealtime } from './layerGroups/linetLightningsMarkerClusterGroup';
import { getPointLayerHisttoric } from './layerGroups/pointLayerHistoric';
import {
  getShapesLayerGroupHistoric,
  getShapesLayerGroupRealTime,
} from './layerGroups/shapesLayerGroup';
import { getUserShapesLayerGroup } from './layerGroups/userShapesLayerGroup';
import {
  getDetailedTileLayer,
  getGoogleHybridTileLayer,
  getNormalTileLayer,
} from './tileLayers/tileLayers';
import { createCustomContainer } from './controls/barControl';
import { sendGeoPointAndGetInfo } from '../../actions/gis';



const useStyles = makeStyles({
  mapContainer: {
    position: 'relative',
    height: '100%',
  },
  map: {
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});

const Gis = ({
  getDataToggle,
  filters,
  playSoundAlert,
  realtime,
  stopSoundAlert,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const [map, setMap] = useState();
  const [realtimeReferences, setRealtimeReferences] = useState([]);
  const [userShapesGeojson, setUserShapesGeojson] = useState(null);
  const [userShapesModalOpen, setUserShapesModalOpen] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    if (realtime) {
      createMapRealtime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      realtimeReferences.forEach((realtimeReference) => {
        realtimeReference.stop();
      });
    };
  }, [realtimeReferences]);

  useEffect(() => {
    if (getDataToggle) {
      if (map) {
        map.remove();
      }
      createMapHistoric();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDataToggle]);

  const createMapRealtime = async () => {
    const defaultBounds = [
      [auth.position.latI, auth.position.lonI],
      [auth.position.latF, auth.position.lonF],
    ];

    const newMap = L.map('mapid', {
      preferCanvas: true,
      zoom: auth.zoom,
      minZoom: auth.zoom,
      zoomControl: false,
    }).fitBounds(defaultBounds);
    setMap(newMap);
    L.control.zoom({ position: "topleft" }).addTo(newMap);

    const normalTileLayer = getNormalTileLayer(auth.zoom);
    normalTileLayer.addTo(newMap);
    const detailedTileLayer = getDetailedTileLayer();
    const googleHybridTileLayer = getGoogleHybridTileLayer();
    newMap.setMaxBounds([
      [auth.position.latI, auth.position.lonI],
      [auth.position.latF, auth.position.lonF],
    ]);
    createCustomContainer(newMap);
    L.control.customContainer({ position: 'topleft' }).addTo(newMap);
    const getPointId = (pointSelected) => {
      const pointID = String(pointSelected);
      console.log({ pointID});
      dispatch(sendGeoPointAndGetInfo({ pointID }));
    };
    const [pointsLayerGroup, pointsRealtimeReference] =
      getPointsLayerGroupRealtime(
        auth.uid,
        auth.colorPoints,
        auth.ptws,
        playSoundAlert,
        stopSoundAlert,
        getPointId
      );
    const [circlesLayerGroup, circleRealtimeReference] =
      getCircleRadiusLayerGroupRealTime(auth.uid, auth.colorPoints, auth.ptws);

    const colorGlm = await getColors(auth.uid, 'rayosglm');
    const [lightningsMarkerClusterGroup, lightningsRealtimeReference] =
      getLightningsMarkerClusterGroupRealtime(auth.uid, colorGlm, auth.ptws);

    const [ligmMarkerClusterGroup, ligmRealtimeReference] =
      getLightningsMarkerClusterGroupRealtime(
        auth.uid,
        colorGlm,
        auth.ptws,
        'LIGL'
      );

    const colorLine = await getColors(auth.uid, 'rayoslinet');
    
    const [announcersLayerGroup, announcersRealtimeReference] =
      getAnnouncersLayerGroupRealtime(auth.uid, auth.colorPoints, auth.ptws);

    const [shapesLayerGroup, shapesRealtimeReference] =
      getShapesLayerGroupRealTime(auth.uid, playSoundAlert, stopSoundAlert, dispatch);
    const [lineLightningsMarkerClusterGroup, lineLightningsRealtimeReference] =
      getLineLightningsMarkerClusterGroupRealtime(
        auth.uid,
        auth.ptws,
        colorLine
     );
    setRealtimeReferences([
      pointsRealtimeReference,
      announcersRealtimeReference,
      circleRealtimeReference,
      shapesRealtimeReference,
      ligmRealtimeReference,
      lightningsRealtimeReference,
      lineLightningsRealtimeReference,
    ]);

    pointsLayerGroup.addTo(newMap);
    if (auth.valitadion.shapes === 'True') {
      shapesLayerGroup.addTo(newMap);
    }
    if (auth.valitadion.announcers === 'True') {
      announcersLayerGroup.addTo(newMap);
    }
    if (auth.valitadion.lineLightnings === 'True') {
      lineLightningsRealtimeReference.addTo(newMap);
    }
    if (auth.valitadion.glmLightnings === 'True') {
      lightningsMarkerClusterGroup.addTo(newMap);
    }
    if (auth.valitadion.radius === 'True') {
      circlesLayerGroup.addTo(newMap);
    }

    if (auth.valitadion.ligmLightnings === 'True') {
      ligmMarkerClusterGroup.addTo(newMap);
    }

    

    const userShapesLayerGroup = getUserShapesLayerGroup(newMap);
    userShapesLayerGroup.addTo(newMap);

    const rulerControl = getRulerControl();
    rulerControl.addTo(newMap);

    if (auth.valitadion.mapControls === 'True') {
      addGeomanControl(newMap);
      const saveControl = getSaveControl(newMap, (newGeojson) => {
        setUserShapesGeojson(newGeojson);
        setUserShapesModalOpen(true);
      });
      saveControl.addTo(newMap);
    }

    const scaleControl = getScaleControl();
    scaleControl.addTo(newMap);
    const mousePositionControl = getMousePositionControl();
    mousePositionControl.addTo(newMap);

    const zoomHomeControl = getZoomHomeControl(defaultBounds);
    zoomHomeControl.addTo(newMap);

    const fullscreenControl = getFullscreenControl();
    newMap.addControl(fullscreenControl);
    const baseLayers = {};
    baseLayers.Normal = normalTileLayer;
    baseLayers.Detallado = detailedTileLayer;
    baseLayers.Satelital = googleHybridTileLayer;

    const overlays = {};

    if (auth.legendtws  !== 'False') {
      overlays.TWS = pointsLayerGroup;
    }
    //if (auth.uid !== 207) {
    //  overlays.TWS = pointsLayerGroup;
    //}    
    if (auth.valitadion.ligmLightnings === 'True') {
      overlays['Rayos'] = ligmMarkerClusterGroup;
    }
    if (auth.valitadion.glmLightnings === 'True') {
      overlays['Rayos'] = lightningsMarkerClusterGroup;
    }
    if (auth.valitadion.lineLightnings === 'True') {
      overlays.Rayos = lineLightningsMarkerClusterGroup;
    }
    if (auth.valitadion.announcers === 'True') {
      overlays.Dispositivos = announcersLayerGroup;
    }
    if (auth.valitadion.radius === 'True') {
      overlays.Radios = circlesLayerGroup;
    }
    if (auth.valitadion.shapes === 'True') {
      overlays.Figuras = shapesLayerGroup;
    }

    if (auth.valitadion.mapControls === 'True') {
      overlays['Formas usuario'] = userShapesLayerGroup;
    }

    const layersControl = getLayersControl(baseLayers, overlays);
    layersControl.addTo(newMap);

    const legendPointsRealTime = await getLegendPoints({
      solicitud: 'labelsMap',
      idCliente: auth.uid,
    });

    const legendsControl = getLegendsControlRealtime(
      auth.legendtws  === 'True' ? pointsLayerGroup : undefined,
      auth.valitadion.ligmLightnings === 'True'
        ? ligmMarkerClusterGroup
        : undefined,
      auth.valitadion.glmLightnings === 'True'
        ? lightningsMarkerClusterGroup
        : undefined,
      auth.valitadion.lineLightnings === 'True'
        ? lineLightningsMarkerClusterGroup
        : undefined,
      auth.valitadion.announcers === 'True' ? announcersLayerGroup : undefined,
      auth.colorPoints,
      colorGlm,
      colorLine,
      auth.valitadion.shapes === 'True' ? shapesLayerGroup : undefined,
      legendPointsRealTime
    );
    legendsControl.addTo(newMap);
  };

  const createMapHistoric = async () => {
    const defaultBounds = [
      [auth.position.latI, auth.position.lonI],
      [auth.position.latF, auth.position.lonF],
    ];


    const centerMap = await dataGeoServiciosFetch(
      {
        solicitud: 'PuntoH',
        punto: filters.pointName,
        idCliente: auth.uid,
      },
      'POST'
    );

    const newMap = L.map('mapid', {
      zoom: 9,
      minZoom: 10,
      center: invertCoordinates(centerMap.features[0].geometry.coordinates),
      preferCanvas: true,
    });
    setMap(newMap);

    const normalTileLayer = getNormalTileLayer();
    normalTileLayer.addTo(newMap);
    const detailedTileLayer = getDetailedTileLayer();
    const googleHybridTileLayer = getGoogleHybridTileLayer();
    const colorHistoric = await getColors(auth.uid, 'rayoshist');
    

    const pointMarker = await getPointLayerHisttoric({
      solicitud: 'PuntoH',
      punto: filters.pointName,
      idCliente: auth.uid,
    });

    const announcersMarker = await getAnnouncersLayerGroupHistoric({
      solicitud: 'Anunciadores',
      idCliente: auth.uid,
    });

    const radiusMaker = await getCircleRadiusLayerGroupHistoric({
      solicitud: 'PuntoH',
      punto: filters.pointName,
      idCliente: auth.uid,
    });

    const shapes = await getShapesLayerGroupHistoric({
      solicitud: 'shpCliente',
      idCliente: auth.uid,
    });
    const lightningsMarkerClusterGroup =
      await getLightningsMarkerClusterGroupHistoric(
        {
          fecI: filters.startDate,
          fecF: filters.endDate,
          punto: filters.pointName,
          id_cliente: auth.uid,
        },
        colorHistoric
      );

    
    pointMarker.addTo(newMap);
    if (auth.valitadion.announcers === 'True') {
      announcersMarker.addTo(newMap);
    }
    if (auth.valitadion.radius === 'True') {
      radiusMaker.addTo(newMap);
    }
    if (auth.valitadion.shapes === 'True') {
      shapes.addTo(newMap);
    }
    lightningsMarkerClusterGroup.addTo(newMap);
    const rulerControl = getRulerControl();
    rulerControl.addTo(newMap);

    const scaleControl = getScaleControl();
    scaleControl.addTo(newMap);
    const mousePositionControl = getMousePositionControl();
    mousePositionControl.addTo(newMap);

    const zoomHomeControl = getZoomHomeControl(defaultBounds);
    zoomHomeControl.addTo(newMap);

    const fullscreenControl = getFullscreenControl();
    newMap.addControl(fullscreenControl);

    const baseLayers = {
      Normal: normalTileLayer,
      Detallado: detailedTileLayer,
      Satelital: googleHybridTileLayer,
    };

    const overlays = {};
    overlays.Rayos = lightningsMarkerClusterGroup;
    if (auth.valitadion.announcers === 'True') {
      overlays.Dispositivos = announcersMarker;
    }
    if (auth.valitadion.radius === 'True') {
      overlays.Radios = radiusMaker;
    }
    if (auth.valitadion.shapes === 'True') {
      overlays.Figuras = shapes;
    }

    const layersControl = getLayersControl(baseLayers, overlays);
    layersControl.addTo(newMap);

    const legendHistoric = await getLegend({
      solicitud: 'labelH',
      fecIL: filters.startDate,
      fecFL: filters.endDate,
      punto: filters.pointName,
      idCliente: auth.uid,
    });

    const legendsControl = getLegendsControlHistoric(
      lightningsMarkerClusterGroup,
      colorHistoric,
      legendHistoric
    );
    legendsControl.addTo(newMap);
  };

  return (
    <div className={classes.mapContainer}>
      <div className={classes.map} id="mapid" />
      <UserShapesModal
        close={() => setUserShapesModalOpen(false)}
        initialGeojson={userShapesGeojson}
        open={userShapesModalOpen}
      />
    </div>
    
  );
};

Gis.propTypes = {
  filters: PropTypes.object,
  getDataToggle: PropTypes.bool,
  playSoundAlert: PropTypes.func,
  realtime: PropTypes.bool.isRequired,
  stopSoundAlert: PropTypes.func,
};

export default Gis;
